import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import TrackerDebugPanel from "./components/TrackerDebugPanel";
import { ScrollToTop } from "./components/ScrollToTop";
import { usePageViewTracking } from "@/hooks/usePageViewTracking";
import { useUtmTracking } from "@/hooks/useUtmTracking";


// Lazy load all route components
const Index = lazy(() => import("./pages/Index"));
const Projects = lazy(() => import("./pages/Projects"));
const Practice = lazy(() => import("./pages/Practice"));
const Articles = lazy(() => import("./pages/Articles"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const RussellLea = lazy(() => import("./pages/projects/RussellLea"));
const Lilyfield = lazy(() => import("./pages/projects/Lilyfield"));
const Duplex = lazy(() => import("./pages/projects/Duplex"));
const CoastalBarn = lazy(() => import("./pages/projects/CoastalBarn"));
const NorthKellyville = lazy(() => import("./pages/projects/NorthKellyville"));
const Annesley = lazy(() => import("./pages/projects/Annesley"));
const SustainableDesign = lazy(() => import("./pages/articles/SustainableDesign"));
const NaturalLight = lazy(() => import("./pages/articles/NaturalLight"));
const IndoorOutdoor = lazy(() => import("./pages/articles/IndoorOutdoor"));
const DaVsCdc = lazy(() => import("./pages/articles/DaVsCdc"));
const BasixRequirements = lazy(() => import("./pages/articles/BasixRequirements"));
const SlopingSites = lazy(() => import("./pages/articles/SlopingSites"));
const BuildingDesignersPatternBooks = lazy(
  () => import("./pages/articles/BuildingDesignersPatternBooks")
);
const LowMidRiseHousing = lazy(() => import("./pages/articles/LowMidRiseHousing"));
const ConstructionBudgeting = lazy(() => import("./pages/articles/ConstructionBudgeting"));
const OwnerBuilder = lazy(() => import("./pages/articles/OwnerBuilder"));
const MaximisingDualOccupancy = lazy(() => import("./pages/articles/MaximisingDualOccupancy"));
const PrePurchaseFeasibilityArticle = lazy(() => import("./pages/articles/PrePurchaseFeasibility"));
const DuplexPreConstructionCosts = lazy(() => import("./pages/articles/DuplexPreConstructionCosts"));
const PrePurchaseFeasibility = lazy(() => import("./pages/PrePurchaseFeasibility"));
const NswPatternBook = lazy(() => import("./pages/NswPatternBook"));
const CdcDaApprovals = lazy(() => import("./pages/CdcDaApprovals"));
const ConstructionCertificate = lazy(() => import("./pages/ConstructionCertificate"));
const Duplexes = lazy(() => import("./pages/Duplexes"));
const AlterationsAdditions = lazy(() => import("./pages/AlterationsAdditions"));
const AirbnbConversions = lazy(() => import("./pages/AirbnbConversions"));
const BoardingHouses = lazy(() => import("./pages/BoardingHouses"));
const MidRise = lazy(() => import("./pages/MidRise"));
const GrannyFlats = lazy(() => import("./pages/GrannyFlats"));
const InteriorDesign = lazy(() => import("./pages/InteriorDesign"));
const NewHomeBuilds = lazy(() => import("./pages/NewHomeBuilds"));
const PoolLandscape = lazy(() => import("./pages/PoolLandscape"));
const Carports = lazy(() => import("./pages/Carports"));
const ProjectManagement = lazy(() => import("./pages/ProjectManagement"));
const Instagram = lazy(() => import("./pages/Instagram"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const WordPressExport = lazy(() => import("./pages/WordPressExport"));
const ScreenshotCapture = lazy(() => import("./pages/ScreenshotCapture"));
const FeasibilityCalculator = lazy(() => import("./pages/FeasibilityCalculator"));
const DuplexYieldCalculator = lazy(() => import("./pages/DuplexYieldCalculator"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const HomeV2 = lazy(() => import("./pages/HomeV2"));
const HomeV4 = lazy(() => import("./pages/HomeV4"));

const queryClient = new QueryClient();

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const AppContent = () => {
  usePageViewTracking();
  useUtmTracking();

  // Strip ?forceHideBadge=true to avoid URL splitting in analytics
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('forceHideBadge')) {
      params.delete('forceHideBadge');
      const newUrl = params.toString() 
        ? `${window.location.pathname}?${params.toString()}` 
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<Pricing />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/russell-lea" element={<RussellLea />} />
          <Route path="/projects/lilyfield" element={<Lilyfield />} />
          <Route path="/projects/duplex" element={<Duplex />} />
          <Route path="/projects/coastal-barn" element={<CoastalBarn />} />
          <Route path="/projects/north-kellyville" element={<NorthKellyville />} />
          <Route path="/projects/annesley" element={<Annesley />} />
          <Route path="/practice" element={<Practice />} />
          <Route path="/articles" element={<Articles />} />
          <Route path="/articles/sustainable-design" element={<SustainableDesign />} />
          <Route path="/articles/natural-light" element={<NaturalLight />} />
          <Route path="/articles/indoor-outdoor" element={<IndoorOutdoor />} />
          <Route path="/articles/da-vs-cdc" element={<DaVsCdc />} />
          <Route path="/articles/basix-requirements" element={<BasixRequirements />} />
          <Route path="/articles/sloping-sites" element={<SlopingSites />} />
          <Route
            path="/articles/building-designers-pattern-books"
            element={<BuildingDesignersPatternBooks />}
          />
          <Route path="/articles/low-mid-rise-housing" element={<LowMidRiseHousing />} />
          <Route path="/articles/construction-budgeting" element={<ConstructionBudgeting />} />
          <Route path="/articles/owner-builder" element={<OwnerBuilder />} />
          <Route path="/articles/maximising-dual-occupancy" element={<MaximisingDualOccupancy />} />
          <Route path="/articles/pre-purchase-feasibility" element={<PrePurchaseFeasibilityArticle />} />
          <Route path="/articles/duplex-pre-construction-costs" element={<DuplexPreConstructionCosts />} />
          <Route path="/pre-purchase-feasibility" element={<PrePurchaseFeasibility />} />
          <Route path="/nsw-pattern-book" element={<NswPatternBook />} />
          <Route path="/cdc-da-approvals" element={<CdcDaApprovals />} />
          <Route path="/construction-certificate" element={<ConstructionCertificate />} />
          <Route path="/duplexes" element={<Duplexes />} />
          <Route path="/alterations-additions" element={<AlterationsAdditions />} />
          <Route path="/airbnb-conversions" element={<AirbnbConversions />} />
          <Route path="/boarding-houses" element={<BoardingHouses />} />
          <Route path="/mid-rise" element={<MidRise />} />
          <Route path="/granny-flats" element={<GrannyFlats />} />
          <Route path="/interior-design" element={<InteriorDesign />} />
          <Route path="/new-home-builds" element={<NewHomeBuilds />} />
          <Route path="/pool-landscape" element={<PoolLandscape />} />
          <Route path="/carports" element={<Carports />} />
          <Route path="/project-management" element={<ProjectManagement />} />
          <Route path="/instagram" element={<Instagram />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />

          {/* WordPress export downloads (works even when the host blocks folder listings) */}
          <Route path="/wordpress-export" element={<WordPressExport />} />
          <Route path="/wordpress-export/download" element={<WordPressExport />} />
          <Route path="/wordpress-export/download.html" element={<WordPressExport />} />
          
          {/* Screenshot capture tool */}
          <Route path="/screenshot-capture" element={<ScreenshotCapture />} />
          
          {/* Feasibility Calculator */}
          <Route path="/feasibility-calculator" element={<FeasibilityCalculator />} />
          <Route path="/duplex-yield-calculator" element={<DuplexYieldCalculator />} />
          <Route path="/admin" element={<AdminLogin />} />

          {/* Hidden internal preview — not linked in nav, excluded from indexing via Helmet noindex */}
          <Route path="/home-v2" element={<HomeV2 />} />
          <Route path="/home-v4" element={<HomeV4 />} />

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppContent />
        <TrackerDebugPanel />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
